import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import {
  Link,
  NavigateFunction,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'; // Import useSearchParams
import Dropdown from '../components/common/Dropdown';
import { useRegisterUserMutation } from '../redux/apiSlice';
import { signupSchema } from '../validations/SchemaValidations';
import {
  ageOptions,
  genderOptions,
  subscriptionOptions,
  referralSourceOptions,
} from '../components/Signup/data';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import TermsConditions from './TermsConditions';
import AlreadyUsedFreeTrialModal from '../components/common/AlreadyUsedFreeTrailModal';

type FormValues = {
  studentFirstName: string;
  parentsEmail: string;
  confirmParentsEmail: string;
  studentsAge: string;
  studentsGender: string;
  subscriptionType: string;
  couponCode: string;
  promoCode: string;
  referralSource: string;
  otherReferralSource: string;
};

type ResendEmailResponse = {
  success: boolean;
  message: string;
};

const SignupSuccessModal = ({
  setIsModalOpen,
  navigate,
  formState,
}: {
  setIsModalOpen: (isModalOpen: boolean) => void;
  navigate: NavigateFunction;
  formState: FormValues;
}) => {
  const [countdown, setCountdown] = useState<number>(0);
  const [isResending, setIsResending] = useState<boolean>(false);

  const handleResendEmail = async () => {
    if (countdown > 0) return;

    setIsResending(true);
    try {
      // Replace this with your actual API call
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user/resend-verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: formState.parentsEmail }),
        },
      );

      const data: ResendEmailResponse = await response.json();

      toast.success('Verification email has been resent');
      setCountdown(60);
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const subscriptionType =
    subscriptionOptions.find(
      (option) => option.id === formState.subscriptionType,
    )?.label || formState.subscriptionType;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
      <div className="flex flex-col items-center justify-center bg-white shadow-lg rounded-lg w-full max-w-md p-8 border border-gray-200">
        <div>
          <strong>Almost there!</strong>
          <br />
          To start your{' '}
          {subscriptionType === 'Free Trial'
            ? 'free 3-day trial'
            : 'subscription'}
          , please verify your email address. We've sent a verification link to{' '}
          <strong>{formState.parentsEmail}</strong>.
          <br />
          <br />
          <strong>Didn't receive the email? </strong> Check your spam folder or
          click here to{' '}
          <button
            onClick={handleResendEmail}
            disabled={countdown > 0 || isResending}
            className={`text-blue-500 hover:text-blue-700 font-medium ${
              countdown > 0 ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            resend
            {countdown > 0 && ` (${countdown}s)`}
          </button>
          {isResending && <span className="ml-2">Resending...</span>}
        </div>
        <button
          className="mt-4 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
          onClick={() => {
            setIsModalOpen(false);
            navigate('/');
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

const SignupUserExistsModal = ({
  setIsModalOpen,
  navigate,
  activeSubscription,
}: {
  setIsModalOpen: (isModalOpen: boolean) => void;
  navigate: NavigateFunction;
  activeSubscription: boolean | string;
}) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
      <div className="flex flex-col items-center justify-center bg-white shadow-lg rounded-lg w-full max-w-md p-8 border border-gray-200">
        <div>
          It looks like you already have an active{' '}
          {activeSubscription ? <strong>{activeSubscription}</strong> : ''}{' '}
          subscription with My Code Dragon
          <br />
          <br />
          You can continue enjoying our features and content by logging into
          your account.
        </div>
        <button
          className="mt-4 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
          onClick={() => {
            setIsModalOpen(false);
            navigate('/login');
          }}
        >
          Login
        </button>
      </div>
    </div>
  );
};

const SignUp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const initialFormState: FormValues = {
    studentFirstName: '',
    parentsEmail: '',
    confirmParentsEmail: '',
    studentsAge: '',
    studentsGender: '',
    subscriptionType: '',
    couponCode: '',
    promoCode: '',
    referralSource: '',
    otherReferralSource: '',
  };

  const [formState, setFormState] = useState<FormValues>(initialFormState);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [registerUser, { data: registerDetailsData, isLoading }] =
    useRegisterUserMutation();
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [termsError, setTermsError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUserExistsModalOpen, setIsUserExistsModalOpen] = useState(false);
  const [isAlreadyUsedFreeTrialModalOpen, setIsAlreadyUsedFreeTrialModalOpen] =
    useState(false);
  const navigate = useNavigate();
  const [promoValidation, setPromoValidation] = useState<{
    isValid: boolean | null;
    message: string;
    validating: boolean;
    isReferral?: boolean;
    coupon?: any;
  }>({
    isValid: null,
    message: '',
    validating: false,
    isReferral: false,
    coupon: null,
  });

  useEffect(() => {
    const subscriptionType = searchParams.get('subscriptionType');
    if (subscriptionType) {
      setFormState((prev) => ({
        ...prev,
        subscriptionType,
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (registerDetailsData) {
      setIsModalOpen(true);
    }
  }, [registerDetailsData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name) {
      setFormState((prevData) => ({ ...prevData, [name]: value }));
    }

    // Reset promo code and validation when subscription type changes
    if (name === 'subscriptionType') {
      setFormState((prevData) => ({ ...prevData, couponCode: '' }));
      setPromoValidation({
        isValid: null,
        message: '',
        validating: false,
      });
    }

    // Reset promo validation when promo field changes
    if (name === 'couponCode') {
      setPromoValidation({
        isValid: null,
        message: '',
        validating: false,
      });
    }

    setFormErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!termsAccepted) {
      setTermsError('Please accept Terms & Conditions');
      return;
    }

    // Check if promo code is entered but not validated or invalid
    if (
      formState.couponCode.trim() &&
      (promoValidation.isValid === null || promoValidation.isValid === false)
    ) {
      toast.error('Please validate your promo code before continuing');
      return;
    }

    const subscriptionType =
      subscriptionOptions.find(
        (option) => option.id === formState.subscriptionType,
      )?.label || formState.subscriptionType;

    // Prepare the payload
    const payload: any = {
      ...formState,
      subscriptionType,
      traffic_source_of_initial_conversion: formState.referralSource,
      traffic_source_details: formState.otherReferralSource || '',
      referralCode: referralCode,
      couponCode: '',
    };
    if (promoValidation.isValid) {
      if (promoValidation.isReferral) {
        payload.userReferralCode = formState.couponCode;
      } else {
        payload.couponCode =
          subscriptionType === 'Annual' ? formState.couponCode : '';
      }
    }
    try {
      await signupSchema.validate(formState, { abortEarly: false });
      setFormErrors({});
      const userDetails = await registerUser(payload).unwrap();
      console.log('register user response', userDetails);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        // Validation errors from Yup
        const errors: { [key: string]: string } = {};
        error.inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });
        setFormErrors(errors);
      } else if (
        typeof error === 'object' &&
        error !== null &&
        'data' in error
      ) {
        // API error handling
        const errorData = (
          error as {
            data: { message?: string | string[]; subscription_set: any };
          }
        ).data;
        const errorMessage = Array.isArray(errorData?.message)
          ? errorData.message[0]
          : errorData?.message || Object.values(errorData)[0]?.[0];
        if (
          errorMessage === 'User already existed validation link send to email'
        ) {
          setIsUserExistsModalOpen(
            errorData?.subscription_set[0]?.plan || true,
          );
        } else if (errorMessage === 'alreadyusedfreetrial') {
          setIsAlreadyUsedFreeTrialModalOpen(true);
        } else {
          toast.error(errorMessage || 'Having trouble signing up?');
        }
      } else {
        // Unknown errors
        toast.error('An unexpected error occurred.');
        console.error('Unknown error:', error);
      }
    }
  };

  const validatecouponCode = async () => {
    if (!formState.couponCode.trim()) {
      setPromoValidation({
        isValid: false,
        message: 'Please enter a promo code',
        validating: false,
      });
      return;
    }

    setPromoValidation((prev) => ({
      ...prev,
      validating: true,
    }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/subscription/coupon-validate/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ coupon: formState.couponCode }),
        },
      );

      const data = await response.json();
      console.log('validate promo code response', data);
      if (data?.valid && data?.id) {
        setFormState((prevData) => ({ ...prevData, promoCode: data?.id }));
        setPromoValidation({
          isValid: true,
          message: '',
          validating: false,
          isReferral: data?.name === 'REFERRAL' ? true : false,
          coupon: data,
        });
      } else {
        setPromoValidation({
          isValid: false,
          message: 'Invalid Promo/Referral Code',
          validating: false,
        });
      }
    } catch (error) {
      setPromoValidation({
        isValid: false,
        message: 'Error validating promo code',
        validating: false,
      });
    }
  };

  const renderInput = (
    id: string,
    name: keyof FormValues,
    type: 'text' | 'email' | 'password' | 'number' | 'date',
    placeholder: string,
  ) => (
    <div className="mb-4">
      <label htmlFor={id} className="text-xs block font-medium">
        {placeholder} {name === 'couponCode' ? '' : '*'}
      </label>
      {name === 'couponCode' ? (
        <div className="relative">
          <Input
            type={type}
            id={id}
            name={name}
            value={formState[name]}
            onChange={handleChange}
            placeholder={`Enter ${placeholder.toLowerCase()}`}
            disabled={isLoading || promoValidation.validating}
            className="pr-[95px]"
          />
          {promoValidation.isValid !== null ? (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              {promoValidation.isValid ? (
                <CheckCircle className="text-green-500 h-5 w-5" />
              ) : (
                <XCircle
                  className="text-red-500 h-5 w-5 cursor-pointer"
                  onClick={() => {
                    setPromoValidation({
                      isValid: null,
                      message: '',
                      validating: false,
                    });
                  }}
                />
              )}
            </div>
          ) : (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <Button
                type="button"
                onClick={validatecouponCode}
                className="cursor-pointer"
                disabled={
                  isLoading ||
                  promoValidation.validating ||
                  !formState.couponCode.trim()
                }
              >
                {promoValidation.validating ? (
                  <Loader2 className="animate-spin h-4 w-4" />
                ) : (
                  'Validate'
                )}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="relative">
          <Input
            type={type}
            id={id}
            name={name}
            value={formState[name]}
            onChange={handleChange}
            placeholder={`Enter ${placeholder.toLowerCase()}`}
            disabled={isLoading}
            className={formState[name] ? 'pr-10' : ''}
          />
        </div>
      )}
      {name === 'couponCode' && promoValidation.isValid !== null && (
        <p
          className={`text-${promoValidation.isValid ? 'green' : 'red'}-500 text-[13px]`}
        >
          {promoValidation.message}
        </p>
      )}
      <p className="text-red-500 text-[13px] h-[4px]">{formErrors[name]}</p>
    </div>
  );

  const renderDropdown = (
    id: string,
    name: keyof FormValues,
    options: Array<{ id: string; value: string; label: string }>,
    placeholder: string,
  ) => (
    <div className="flex-1">
      <label htmlFor={id} className="text-xs block font-medium">
        {name
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/^./, (str) => str.toUpperCase())}{' '}
        *
      </label>
      <Dropdown
        id={id}
        name={name}
        options={options}
        value={formState[name]}
        onSelect={handleChange}
        placeholder={placeholder}
        disabled={isLoading}
      />
      <p className="text-red-500 text-[13px] h-[4px]">{formErrors[name]}</p>
    </div>
  );

  useEffect(() => {
    if (termsAccepted) {
      setTermsError('');
    }
  }, [termsAccepted]);

  return (
    <div className="flex items-start justify-center min-h-screen mt-10">
      <div className="bg-white p-8 shadow-[0_0_15px_rgba(0,0,0,0.2)] max-w-2xl w-full rounded-lg">
        <h1 className="text-[24px] font-bold mb-4">Start Coding Today</h1>
        <form onSubmit={handleSubmit}>
          {renderInput(
            'studentFirstName',
            'studentFirstName',
            'text',
            "Student's First Name",
          )}
          {renderInput(
            'parentsEmail',
            'parentsEmail',
            'email',
            'Email Address',
          )}
          {renderInput(
            'confirmParentsEmail',
            'confirmParentsEmail',
            'email',
            'Confirm Email Address',
          )}
          <div className="flex flex-col md:flex-row md:space-x-4">
            {renderDropdown('studentsAge', 'studentsAge', ageOptions, 'Age')}
            {renderDropdown(
              'studentsGender',
              'studentsGender',
              genderOptions,
              'Gender',
            )}
            {renderDropdown(
              'subscriptionType',
              'subscriptionType',
              subscriptionOptions,
              'Subscription',
            )}
          </div>
          <div className="mt-4">
            {renderDropdown(
              'referralSource',
              'referralSource',
              referralSourceOptions,
              'How did you hear about us?',
            )}
            {formState.referralSource === 'other' && (
              <div className="mt-2">
                {renderInput(
                  'otherReferralSource',
                  'otherReferralSource',
                  'text',
                  'Please specify how you heard about us',
                )}
              </div>
            )}
          </div>
          {(formState.subscriptionType === '2' ||
            formState.referralSource === 'referral') && (
            <div className="mt-4">
              {renderInput(
                'couponCode',
                'couponCode',
                'text',
                'Promo/Referral Code',
              )}
            </div>
          )}
          <div style={{ marginTop: '20px' }}>
            <label>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={() => setTermsAccepted(!termsAccepted)}
              />{' '}
              I accept the Terms and Conditions.
            </label>
            <TermsConditions
              subscriptionType={formState?.subscriptionType}
              promo={promoValidation}
            />
            {/* <p
              className="ml-4 text-[10px] cursor-pointer"
              onClick={() => setIsModalOpen(true)}
            >
              See more
            </p> */}
          </div>
          <span className="mt-4 text-red-600 m-auto">{termsError}</span>
          <div className="flex justify-center">
            <Button
              type="submit"
              className="w-[60%] h-[50px] mt-5 flex items-center justify-center text-[24px]"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="animate-spin h-6 w-6" />
              ) : (
                'Sign Up'
              )}
            </Button>
          </div>
        </form>
        <p className="mt-4 text-center">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-blue-500">
            Login here
          </Link>
        </p>
      </div>
      {isModalOpen && (
        <SignupSuccessModal
          setIsModalOpen={setIsModalOpen}
          navigate={navigate}
          formState={formState}
        />
      )}
      {isUserExistsModalOpen && (
        <SignupUserExistsModal
          setIsModalOpen={setIsUserExistsModalOpen}
          activeSubscription={isUserExistsModalOpen}
          navigate={navigate}
        />
      )}
      {isAlreadyUsedFreeTrialModalOpen && (
        <AlreadyUsedFreeTrialModal
          onClose={() => setIsAlreadyUsedFreeTrialModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SignUp;
